import { RoomEvent, type RemoteParticipant, type RemoteTrack, type RemoteTrackPublication, type Room } from 'livekit-client'

/** A short cushion for broadcast delivery jitter, shared by video and audio. */
export const BROADCAST_PLAYOUT_MS = 500

type Receiver = { jitterBufferTarget?: number | null; playoutDelayHint?: number | null }

/** Apply only to camera/programme publishers; live host conversations keep their normal latency. */
export function bufferBroadcastTracks(room: Room): () => void {
  const restore = new Map<RemoteTrack, () => void>()
  const subscribed = (track: RemoteTrack, _publication: RemoteTrackPublication, participant: RemoteParticipant) => {
    if (!participant.identity.startsWith('stream_') || restore.has(track)) return
    const receiver = track.receiver as Receiver | undefined
    if (!receiver) return
    // jitterBufferTarget is the current API (milliseconds); older Chromium uses
    // playoutDelayHint (seconds). Unsupported browsers retain their own default.
    const property = 'jitterBufferTarget' in receiver ? 'jitterBufferTarget'
      : 'playoutDelayHint' in receiver ? 'playoutDelayHint' : null
    if (!property) return
    const previous = receiver[property]
    const target = property === 'jitterBufferTarget' ? BROADCAST_PLAYOUT_MS : BROADCAST_PLAYOUT_MS / 1000
    try {
      receiver[property] = target
    } catch {
      return // A browser rejecting the hint must never prevent playback.
    }
    restore.set(track, () => {
      if (track.receiver === receiver && receiver[property] === target) {
        try { receiver[property] = previous } catch { /* The receiver may have ended. */ }
      }
    })
  }
  const unsubscribed = (track: RemoteTrack) => {
    restore.get(track)?.()
    restore.delete(track)
  }
  const reset = () => {
    restore.forEach((restoreTrack) => restoreTrack())
    restore.clear()
  }
  room.on(RoomEvent.TrackSubscribed, subscribed)
  room.on(RoomEvent.TrackUnsubscribed, unsubscribed)
  room.on(RoomEvent.Disconnected, reset)
  for (const participant of room.remoteParticipants.values()) {
    for (const publication of participant.trackPublications.values()) {
      if (publication.track) subscribed(publication.track, publication, participant)
    }
  }
  return () => {
    room.off(RoomEvent.TrackSubscribed, subscribed)
    room.off(RoomEvent.TrackUnsubscribed, unsubscribed)
    room.off(RoomEvent.Disconnected, reset)
    reset()
  }
}
